import Client from 'App/resources/Model/Client';
import { Feature, isAvailable, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import AlertConfirm from 'Components/Dialogs/AlertConfirm';
import AlertBody from 'Components/Dialogs/Body/AlertBody';
import React, { lazy } from 'react';
import T from 'Components/Helpers/translate';
import openModal from 'App/services/Modal';
import { getConfirmationTitle } from 'App/components/Alerts/ConfirmationTexts';
import config from 'App/babel/config';
import AppService from 'App/legacy/AppService';

export const calculateRelationsCount = (client: Client, subsidiaries: { metadata: { total: number } }) => {
	const inCompanyGroup = () => {
		const hasProspectingPro = isAvailable(Feature.PROSPECTING_PRO);
		const hasProspectingBasic = isAvailable(Feature.PROSPECTING_BASIC);
		const hasGroupSize = isAvailable(Feature.GROUP_SIZE);
		const hasCompanyGroupCard = isAvailable(Feature.COMPANY_GROUP_CARD);
		const showCommercial = !hasGroupSize && !hasProspectingPro && hasProspectingBasic;

		if (client.prospecting && hasCompanyGroupCard && hasProspectingPro) {
			return 0;
		} else if (client.prospecting && (showCommercial || hasProspectingPro)) {
			return client.prospecting.groupSize > 1 ? 1 : 0;
		}

		return 0;
	};

	const parentRelationsCount = client.parent?.id ? 1 : 0;

	return client.connectedClients.length + subsidiaries.metadata.total + inCompanyGroup() + parentRelationsCount;
};

export const confirmDeleteClient = (client: Client) => {
	const customerId = AppService.getCustomerId();
	const alertOptions = {
		type: 'confirm',
		reactive: true,
		fullscreen: true,
		hideAbort: false,
		dialog: AlertConfirm,
		id: 'confirm-delete-fields',
		body: React.createElement(AlertBody, {
			account: client,
			customerId: client.id,
			entity: 'Client',
			numSelected: 1
		}),
		confirmButtonText: T('admin.modal.deleteEntityAndRelated', { entity: T('account') }),
		confirmClass: 'btn-red',
		confirmationMode: 'text',
		confirmText: client.name,
		confirmFn: () =>
			Tools.Account.customer(customerId)
				.delete(client)
				.then(() => {
					// Fine for now, should go somewhere else in final version
					Tools.$state.go('accounts', {});
				})
	};

	openModal('Alert', {
		...alertOptions,
		title: getConfirmationTitle('Client', 1, client.name),
		onClose: confirmed => {
			if (confirmed) {
				alertOptions.confirmFn();
			}
		}
	});
};

export const contentMap = {
	activities: lazy(() => import('./ContentPages/Activities')),
	contacts: lazy(() => import('./ContentPages/Contacts')),
	overview: lazy(() => import('./ContentPages/Overview')),
	sales: lazy(() => import('./ContentPages/Sales')),
	relations: lazy(() => import('./ContentPages/Relations')),
	marketing: lazy(() => import('./ContentPages/Marketing/Marketing')),
	opportunities: lazy(() => import('./ContentPages/Opportunities/Opportunities')),
	financial: lazy(() => import('./ContentPages/CompanyFinancials')),
	documents: lazy(() => import('./ContentPages/Documents')),
	esign: lazy(() => import('./ContentPages/ESigns')),
	apps: lazy(() => import('./ContentPages/Apps')),
	support: lazy(() => import('./ContentPages/Support')),
	projectPlan: lazy(() => import('./ContentPages/ProjectPlan')),
	history: lazy(() => import('./ContentPages/History'))
};

export const reactToAngularRedirectMap = {
	overview: '',
	contacts: 'contacts/',
	activities: 'activities/',
	opportunities: 'opportunities/',
	sales: 'orders/',
	financial: 'financial/',
	marketing: 'market/',
	relations: 'relations/',
	documents: 'files/',
	esign: 'esign/',
	apps: 'apps/',
	support: 'support/',
	projectPlan: 'projectplan/',
	history: 'history/',
	// Sidebar-only destination: the sidebar's "show all addresses" link and the
	// order modal's visit-address link both target the Angular `account.addresses`
	// state, and there is no React content page for it. It belongs here and NOT in
	// `contentMap` so the redirect carries the segment instead of falling back to
	// '' — which landed the user on the dashboard and dropped the destination.
	addresses: 'addresses/'
} as const;

export type PageName = keyof typeof contentMap | keyof typeof reactToAngularRedirectMap | `udo-${number}`;

Tools.clientCardDev = {
	get useAngular() {
		const ls = localStorage.getItem('cc.useAngular');
		if (ls === null) {
			return null;
		}
		return ls === 'true';
	},
	set useAngular(v) {
		if (v === null) {
			localStorage.removeItem('cc.useAngular');
			return;
		}
		localStorage.setItem('cc.useAngular', String(v));
	}
};

declare global {
	namespace Tools {
		let clientCardDev: {
			get useAngular(): boolean | null;
			set useAngular(v: boolean | null);
		};
	}
}

export const shouldUseAngularPage = (pageName: PageName) => {
	const hasReactClientCard = hasSoftDeployAccess('REACT_CLIENT_CARD');
	if (!hasReactClientCard) {
		return true;
	}

	if (config.ENV === 'DEV' && Tools.clientCardDev.useAngular !== null) {
		return Tools.clientCardDev.useAngular;
	}

	if (typeof pageName === 'string' && pageName.startsWith('udo-')) {
		return false;
	}

	if (!contentMap.hasOwnProperty(pageName)) {
		return true;
	}

	return false;
};
